import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Admin", manager: "Manager", calling_executive: "Calling Executive" };
const blank = { name: "", email: "", password: "", role: "calling_executive" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); toast.success(u.active ? "Deactivated" : "Activated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary">Team</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">User Management</h1>
        </div>
        <Button onClick={openNew} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="add-user-btn"><UserPlus className="h-4 w-4" /> Add user</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-3 px-4">Name</th><th className="px-4">Email</th><th className="px-4">Role</th><th className="px-4">Status</th><th className="px-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted transition-colors" data-testid={`user-row-${u.email}`}>
                <td className="py-3 px-4 font-medium">{u.name}</td>
                <td className="px-4 text-muted-foreground">{u.email}</td>
                <td className="px-4"><span className="text-xs font-bold uppercase tracking-wide">{ROLE_LABELS[u.role]}</span></td>
                <td className="px-4">
                  {u.role === "admin" ? <span className="text-xs text-muted-foreground">—</span> : (
                    <div className="flex items-center gap-2"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`} /><span className="text-xs">{u.active ? "Active" : "Inactive"}</span></div>
                  )}
                </td>
                <td className="px-4 text-right">
                  {u.role !== "admin" && <Button size="sm" variant="ghost" className="rounded-none" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">{editing ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input className="rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input className="rounded-none" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div className="space-y-2"><Label>{editing ? "New password (optional)" : "Password"}</Label><Input className="rounded-none" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-none" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="manager">Manager</SelectItem><SelectItem value="calling_executive">Calling Executive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-none font-bold uppercase tracking-wide w-full" data-testid="save-user">{editing ? "Save changes" : "Create user"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
